/**
 * CallFlow Logs Tab Component
 *
 * View execution logs with filtering by event type.
 */

import { useEffect, useState } from 'react';
import { callFlowService, CallFlowExecutionLog } from '@/services/callFlowService';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Loader2, RefreshCw } from 'lucide-react';

interface CallFlowLogsTabProps {
  organizationId: string;
  callflowId: string;
}

export function CallFlowLogsTab({ callflowId, organizationId }: CallFlowLogsTabProps) {
  const [logs, setLogs] = useState<CallFlowExecutionLog[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [limit] = useState(100);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    fetchLogs();

    // Auto-refresh logs every 10 seconds
    const interval = setInterval(fetchLogs, 10000);
    return () => clearInterval(interval);
  }, [callflowId, eventTypeFilter, offset]);

  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      const filters: any = { limit, offset };
      if (eventTypeFilter !== 'all') {
        filters.event_type = eventTypeFilter;
      }

      const { logs: data, total: totalCount } = await callFlowService.getLogs(
        callflowId,
        filters
      );
      setLogs(data);
      setTotal(totalCount);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load execution logs',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getEventBadge = (eventType: string) => {
    const colors: Record<string, string> = {
      started: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      resumed: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      call_initiated: 'bg-blue-100 text-blue-800',
      call_completed: 'bg-green-100 text-green-800',
      call_failed: 'bg-red-100 text-red-800',
      lead_queued: 'bg-gray-100 text-gray-800',
      phone_selected: 'bg-purple-100 text-purple-800',
      retry_scheduled: 'bg-orange-100 text-orange-800',
      rate_limit_hit: 'bg-yellow-100 text-yellow-800',
      concurrency_limit_hit: 'bg-yellow-100 text-yellow-800',
      worker_heartbeat: 'bg-gray-100 text-gray-800',
      error: 'bg-red-100 text-red-800',
    };

    return (
      <Badge className={colors[eventType] || 'bg-gray-100 text-gray-800'}>
        {eventType.replace(/_/g, ' ').toUpperCase()}
      </Badge>
    );
  };

  const formatDetails = (details: any) => {
    if (!details || Object.keys(details).length === 0) return null;

    return (
      <pre className="text-xs bg-slate-800 p-2 rounded mt-2 overflow-auto max-w-full">
        {JSON.stringify(details, null, 2)}
      </pre>
    );
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium">Event Type:</label>
          <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
            <SelectTrigger className="w-[250px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="started">Started</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="resumed">Resumed</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="call_initiated">Call Initiated</SelectItem>
              <SelectItem value="call_completed">Call Completed</SelectItem>
              <SelectItem value="call_failed">Call Failed</SelectItem>
              <SelectItem value="lead_queued">Lead Queued</SelectItem>
              <SelectItem value="phone_selected">Phone Selected</SelectItem>
              <SelectItem value="retry_scheduled">Retry Scheduled</SelectItem>
              <SelectItem value="rate_limit_hit">Rate Limit Hit</SelectItem>
              <SelectItem value="concurrency_limit_hit">Concurrency Limit Hit</SelectItem>
              <SelectItem value="worker_heartbeat">Worker Heartbeat</SelectItem>
              <SelectItem value="error">Error</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button variant="outline" onClick={fetchLogs} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Logs */}
      {isLoading && logs.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            {eventTypeFilter === 'all'
              ? 'No execution logs yet'
              : `No logs for event type "${eventTypeFilter}"`}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {logs.map((log) => (
            <Card key={log.id}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getEventBadge(log.event_type)}
                      <span className="text-sm text-gray-500">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>
                    {log.message && (
                      <p className="text-sm font-medium mb-1">{log.message}</p>
                    )}
                    <div className="flex gap-4 text-xs text-gray-600">
                      {log.callflow_lead_id && (
                        <span>Lead ID: {log.callflow_lead_id.substring(0, 8)}...</span>
                      )}
                      {log.call_log_id && (
                        <span>Call ID: {log.call_log_id.substring(0, 8)}...</span>
                      )}
                      {log.phone_number_id && (
                        <span>Phone ID: {log.phone_number_id.substring(0, 8)}...</span>
                      )}
                    </div>
                    {log.details && formatDetails(log.details)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination Info */}
      <div className="text-sm text-gray-600 text-center">
        Showing {logs.length} of {total} logs
      </div>
    </div>
  );
}
